import { useState, useCallback, useRef, useEffect } from 'react';
import { useGameStore, BOARD_ROWS, BOARD_COLS } from '../store/useGameStore';
import { getShapeMatrix, getShapeCells } from '../data/shapes';
import { COLOR_CSS, ELEMENT_CSS } from '../data/attributes';
import type { Stone, StoneColor, ElementType } from '../types';
import './Cell.css';

interface CellProps {
    row: number;
    col: number;
    active: boolean;
    occupied: boolean;
    stoneId: string | null;
    isHovering: boolean;
    canPlace: boolean;
    onDragOver: (e: React.DragEvent) => void;
    onDragLeave: () => void;
    onDrop: (e: React.DragEvent) => void;
    onCellClick: () => void;
}

export function Cell({ row, col, active, occupied, isHovering, canPlace, onDragOver, onDragLeave, onDrop, onCellClick }: CellProps) {
    const cls = [
        'cell',
        active ? 'active' : 'inactive',
        occupied ? 'occupied' : 'empty',
        isHovering ? (canPlace ? 'hover-valid' : 'hover-invalid') : '',
    ].filter(Boolean).join(' ');

    return (
        <div
            className={cls}
            data-row={row}
            data-col={col}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onDrop={onDrop}
            onClick={onCellClick}
        >
            {active && <div className="cell-inner" />}
        </div>
    );
}

interface StoneOverlayProps {
    stone: Stone;
    anchorRow: number;
    anchorCol: number;
    board: any[][];
}

/** 在棋盘上渲染石头覆盖层 */
export function StoneOverlay({ stone, anchorRow, anchorCol, board }: StoneOverlayProps) {
    // 获取石头的形状矩阵
    const matrix = getShapeMatrix(stone.shape, stone.currentRotation);
    const cells = getShapeCells(stone.shape, stone.currentRotation);
    const color = COLOR_CSS[stone.color];
    const borderColor = stone.color === 'white' ? '#aaa' : color;

    // 计算石头的边界
    let minR = anchorRow, maxR = anchorRow, minC = anchorCol, maxC = anchorCol;
    for (const [dr, dc] of cells) {
        minR = Math.min(minR, anchorRow + dr);
        maxR = Math.max(maxR, anchorRow + dr);
        minC = Math.min(minC, anchorCol + dc);
        maxC = Math.max(maxC, anchorCol + dc);
    }

    return (
        <div
            className="stone-overlay"
            style={{
                gridRow: `${minR + 1} / ${maxR + 2}`,
                gridColumn: `${minC + 1} / ${maxC + 2}`,
                background: `linear-gradient(135deg, ${color}44, ${color}22)`,
                border: `2px solid ${borderColor}`,
                borderRadius: '6px',
                boxShadow: `0 0 12px ${color}66, inset 0 0 8px ${color}44`,
                pointerEvents: 'none',
                zIndex: 1,
            }}
        >
            {/* 元素装饰 */}
            {stone.attributes.some(a => a.element) && (
                <div className="stone-element-glow"
                     style={{
                         background: `radial-gradient(circle, ${ELEMENT_CSS[stone.attributes.find(a => a.element)?.element || 'fire']}44, transparent)`,
                     }}
                />
            )}
        </div>
    );
}